/**
 * MyLearningPage — agent portal home. Dashboard stats + assigned learning cards.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle2, Clock, AlertTriangle, Award, Timer } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import AgentReadinessCard from './AgentReadinessCard';

const STATUS_BADGE = {
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800' },
  in_progress: { label: 'In Progress', cls: 'bg-warm-200 text-warm-800' },
  overdue: { label: 'Overdue', cls: 'bg-rose-100 text-rose-800' },
  pending: { label: 'Not Started', cls: 'bg-slate-100 text-slate-700' },
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xl font-bold font-display leading-none">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </Card>
  );
}

export default function MyLearningPage() {
  const { get } = useApi();
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([get('/me/learning/dashboard'), get('/me/learning')])
      .then(([s, a]) => { if (!cancelled) { setStats(s); setAssignments(a.assignments || []); } })
      .catch(e => { if (!cancelled) setLoadError(e.response?.data?.detail || 'Could not load your learning. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [get]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading your learning…</p>;

  if (loadError) return (
    <div className="space-y-6" data-testid="my-learning-page">
      <h1 className="font-display text-3xl font-bold tracking-tight">My Learning</h1>
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="my-learning-error">
        <p className="font-semibold text-sm text-amber-900">Couldn't load your learning</p>
        <p className="text-sm text-amber-800 mt-0.5">{loadError}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="my-learning-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">My Learning</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Complete your assigned learning, track progress, and take quizzes.
        </p>
      </div>

      <AgentReadinessCard />

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={BookOpen} label="Assigned" value={stats.assigned} testid="stat-assigned" />
          <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} testid="stat-completed" />
          <StatCard icon={Clock} label="Pending" value={stats.pending} testid="stat-pending" />
          <StatCard icon={AlertTriangle} label="Overdue" value={stats.overdue} testid="stat-overdue" />
          <StatCard icon={Award} label="Avg Quiz" value={stats.avg_quiz_score != null ? `${stats.avg_quiz_score}%` : '—'} testid="stat-quiz" />
          <StatCard icon={Timer} label="Learning Hrs" value={stats.learning_hours} testid="stat-hours" />
        </div>
      )}

      {assignments.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="font-display text-lg font-semibold">Nothing assigned yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Your trainer will assign learning material here after classroom sessions.
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="assignment-list">
          {assignments.map(a => {
            const badge = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
            return (
              <Card key={a.assignment_id} className="p-4" data-testid={`assignment-card-${a.assignment_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold text-base">{a.title}</h3>
                      <Badge className={`${badge.cls} border-0`}>{badge.label}</Badge>
                      {a.has_quiz && <Badge variant="outline" className="text-[10px]">Quiz</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Assigned by: {a.assigned_by || '—'}
                      {a.due_date && <> · Due: {new Date(a.due_date).toLocaleDateString()}</>}
                      {' '}· {a.item_count} item{a.item_count === 1 ? '' : 's'}
                      {a.quiz_score != null && <> · Quiz: {a.quiz_score}%</>}
                    </p>
                  </div>
                  <Button asChild size="sm" data-testid={`continue-btn-${a.assignment_id}`}>
                    <Link to={`/my-learning/${a.assignment_id}`}>
                      {a.status === 'completed' ? 'Review' : a.status === 'pending' ? 'Start Learning' : 'Continue Learning'}
                    </Link>
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Progress value={a.progress_pct} className="h-2 flex-1" />
                  <span className="text-xs font-medium w-10 text-right">{Math.round(a.progress_pct)}%</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
